import { useCallback, useEffect, useState } from 'react'
import { Clock, Sparkles } from 'lucide-react'
import {
  OrchestratorDeleteDialog,
  OrchestratorHistory,
  OrchestratorPane
} from '../../../components/Chat/OrchestratorPane'
import { TabStrip } from '../../../components/Chat/TabStrip'
import { useOrchestratorChatController } from '../../../components/Chat/useOrchestratorChatController'
import { Button } from '../../../components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/tooltip'

/**
 * Assist chat rail — same chrome as GlobalQuerySidebar (`OrchestratorPane` +
 * tabs + history), running the query agent directly without graph-write
 * capability. No work-order scoping.
 */
export function AssistChat() {
  const chat = useOrchestratorChatController({ mode: 'query_agent_only' })
  const [showHistory, setShowHistory] = useState(false)
  const { loadConversations } = chat

  useEffect(() => {
    void loadConversations().catch((err) => {
      console.warn('[assist-chat] conversation-list load failed', err)
    })
  }, [loadConversations])

  const closeHistory = useCallback(() => setShowHistory(false), [])

  return (
    <div className="tv-assist flex h-full min-h-0 w-full flex-col bg-background text-foreground">
      <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Sparkles className="size-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-sm font-semibold text-foreground">Industrial Agent</h2>
          <p className="m-0 text-11 text-muted-foreground">Talk to your factory.</p>
        </div>
        <TooltipProvider delayDuration={700}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={showHistory ? 'secondary' : 'ghost'}
                size="icon-sm"
                aria-label={showHistory ? 'Show chat' : 'Show conversation history'}
                onClick={() => {
                  if (!showHistory) {
                    void loadConversations().catch(() => undefined)
                  }
                  setShowHistory((value) => !value)
                }}
              >
                <Clock className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {showHistory ? 'Show chat' : 'Conversation history'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </header>

      {showHistory ? (
        <OrchestratorHistory
          chat={chat}
          onClose={closeHistory}
          onFocusOrchestrator={closeHistory}
        />
      ) : (
        <>
          {chat.openTabSummaries.length > 0 ? (
            <TabStrip
              tabs={chat.openTabSummaries}
              activeTabId={chat.activeTabId}
              onSwitch={(id) => chat.handleSwitchTab(id)}
              onClose={chat.handleCloseTab}
              onNew={chat.handleNewTab}
            />
          ) : null}
          <OrchestratorPane chat={chat} emptyStatePrompts={[]} />
        </>
      )}
      <OrchestratorDeleteDialog chat={chat} />
    </div>
  )
}
